import React, { useMemo, useCallback, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  Brush,
} from 'recharts';
import {
  DollarSign,
  List,
  CreditCard,
  AlertCircle,
  Loader2,
  Sparkles,
  RefreshCcw,
  ArrowUpRight,
  TrendingUp,
  PiggyBank,
} from 'lucide-react';
import { formatCurrency, formatDateTimeDisplay } from '../../utils/formatters';

const Dashboard = ({

  transactions,

  summary,

  loading,

  onEditTransaction,

  onCategoryFilter = () => {},

  onBankFilter = () => {},

  onRangeFilter = () => {},

  onDataRefresh = () => {},

}) => {

  const {

    totalSpend = 0,

    spendByBank = [],

    spendByCategory = [],

    spendOverTime = [],

    weeklyTrend = [],

    topMerchants = [],

    budgetPerformance = [],

    aiBudgetCoach = null,

    inflowOutflow = null,

    accounts = [],

  } = summary || {};



  const nonInternalTransactions = transactions.filter(

    (txn) => txn.category !== 'Internal (CC Payment)' && txn.category !== 'Internal (Transfer)'

  );

  const averageTransaction =

    nonInternalTransactions.length > 0

      ? nonInternalTransactions.reduce((sum, txn) => sum + parseFloat(txn.amount), 0) /

        nonInternalTransactions.length

      : 0;

  const topCategory = spendByCategory[0];

  const topBank = spendByBank[0];

  const weeklyTrendChartData = weeklyTrend.map((item) => ({

    ...item,

    label: new Date(item.name).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),

  }));



  const handleCategoryBarClick = useCallback(

    (data, index) => {

      const categoryName = data?.payload?.name || spendByCategory[index]?.name;

      if (categoryName) {

        onCategoryFilter(categoryName);

      }

    },

    [onCategoryFilter, spendByCategory]

  );



  const handleBankBarClick = useCallback(

    (data, index) => {

      const bankName = data?.payload?.bank || spendByBank[index]?.bank || data?.payload?.name;

      if (bankName) {

        onBankFilter(bankName);

      }

    },

    [onBankFilter, spendByBank]

  );



  const handleMonthlyBrushChange = useCallback(

    (range) => {

      if (!range || range.startIndex == null || range.endIndex == null) return;

      const startEntry = spendOverTime[range.startIndex];

      const endEntry = spendOverTime[range.endIndex];

      if (!startEntry || !endEntry) return;

      const startDate = `${startEntry.name}-01`;

      const endDateObj = new Date(`${endEntry.name}-01`);

      const lastDay = new Date(endDateObj.getFullYear(), endDateObj.getMonth() + 1, 0);

      onRangeFilter(startDate, lastDay.toISOString().split('T')[0]);

    },

    [spendOverTime, onRangeFilter]

  );



  const handleWeeklyBrushChange = useCallback(

    (range) => {

      if (!range || range.startIndex == null || range.endIndex == null) return;

      const startEntry = weeklyTrend[range.startIndex];

      const endEntry = weeklyTrend[range.endIndex];

      if (!startEntry || !endEntry) return;

      const startDate = startEntry.name;

      const endDate = new Date(endEntry.name);

      endDate.setDate(endDate.getDate() + 6);

      onRangeFilter(startDate, endDate.toISOString().split('T')[0]);

    },

    [weeklyTrend, onRangeFilter]

  );



  if (loading) {
    return (
      <div className="glass-panel flex h-64 items-center justify-center">
        <Loader2 className="animate-spin text-brand-500" size={48} />
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="glass-panel text-center">
        <h2 className="font-display text-3xl text-slate-900">Welcome to your new command center</h2>
        <p className="mt-3 text-slate-600">
          Upload your first statement from the Upload tab to unlock insights, charts, and smart coaching.
        </p>
      </div>
    );
  }



  const recentTransactions = [...transactions]

    .sort((a, b) => new Date(b.transaction_date) - new Date(a.transaction_date))

    .slice(0, 20);



  return (
    <div className="space-y-10">
      <HeroBanner
        totalSpend={totalSpend}
        transactionCount={transactions.length}
        topCategory={topCategory}
        topBank={topBank}
        onRefresh={onDataRefresh}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Total Tracked Spend" value={formatCurrency(totalSpend, 0)} icon={DollarSign} tone="brand" />
        <StatCard title="Total Transactions" value={transactions.length} helper="All time" icon={List} tone="sky" />
        <StatCard
          title="Average Transaction"
          value={formatCurrency(averageTransaction, 2)}
          helper="Excludes transfers"
          icon={TrendingUp}
          tone="teal"
        />
        <StatCard
          title="Top Category"
          value={topCategory?.name || 'N/A'}
          helper={topCategory ? formatCurrency(topCategory.value, 0) : 'Awaiting data'}
          icon={CreditCard}
          tone="amber"
        />
      </div>



      <InflowOutflowCard data={inflowOutflow} />



      <BudgetProgress budgets={budgetPerformance} />



      <AccountBalancesPanel accounts={accounts} onSnapshotSaved={onDataRefresh} />

      <BudgetCoachPanel insight={aiBudgetCoach} />



      {/* Charts */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

        <ChartCard title="Spend by Category">

          <ResponsiveContainer width="100%" height={300}>

            <BarChart data={spendByCategory.slice(0, 10)} layout="vertical" margin={{ right: 30, left: 20 }}>

              <CartesianGrid strokeDasharray="3 3" />

              <XAxis type="number" />

              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />

              <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />

              <Bar dataKey="value" fill="#4f46e5" onClick={handleCategoryBarClick} cursor="pointer" />

            </BarChart>

          </ResponsiveContainer>

        </ChartCard>



        <ChartCard title="Monthly Spend Trend">

          <ResponsiveContainer width="100%" height={300}>

            <LineChart data={spendOverTime} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>

              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="name" />

              <YAxis />

              <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />

              <Legend />

              <Line type="monotone" dataKey="value" stroke="#4f46e5" strokeWidth={2} name="Monthly Spend" />

              <Brush dataKey="name" height={20} stroke="#4f46e5" travellerWidth={8} onChange={handleMonthlyBrushChange} />

            </LineChart>

          </ResponsiveContainer>

        </ChartCard>

      </div>



      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

        <ChartCard title="Weekly Spending Pulse">

          <ResponsiveContainer width="100%" height={300}>

            <LineChart data={weeklyTrendChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>

              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="label" />

              <YAxis />

              <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />

              <Legend />

              <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} name="Weekly Spend" />

              <Brush dataKey="label" height={20} stroke="#10b981" travellerWidth={8} onChange={handleWeeklyBrushChange} />

            </LineChart>

          </ResponsiveContainer>

        </ChartCard>



        <ChartCard title="Spend by Bank">

          <ResponsiveContainer width="100%" height={300}>

            <BarChart data={spendByBank} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>

              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="bank" hide />

              <YAxis />

              <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />

              <Legend />

              <Bar dataKey="value" name="Spend" fill="#f59e0b" onClick={handleBankBarClick} cursor="pointer" />

            </BarChart>

          </ResponsiveContainer>

        </ChartCard>

      </div>



      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">

        <TopMerchantsTable merchants={topMerchants} />

        <InsightsPanel budgets={budgetPerformance} categories={spendByCategory} topBank={topBank} />

      </div>



      {/* Recent Transactions */}

      <div className="rounded-3xl border border-white/30 bg-white/95 p-6 shadow-panel">

        <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">

          <div>

            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-500">Activity</p>

            <h3 className="text-2xl font-semibold text-slate-900">Recent Transactions</h3>

          </div>

          <p className="text-xs text-slate-400">

            Showing {recentTransactions.length} most recent records

          </p>

        </div>



        <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-100 bg-white soft-scrollbar">

          <table className="min-w-full divide-y divide-slate-100 text-sm">

            <thead className="bg-slate-50/70 text-slate-500">

              <tr>

                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide">Date</th>

                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide">Description</th>

                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide">Category</th>

                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide">Amount</th>

                <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide">Actions</th>

              </tr>

            </thead>

            <tbody className="divide-y divide-slate-100 text-slate-600">

              {recentTransactions.map((txn) => (

                <tr key={txn.id} className="hover:bg-slate-50/80">

                  <td className="px-5 py-3 whitespace-nowrap">

                    {new Date(txn.transaction_date).toLocaleDateString()}

                  </td>

                  <td className="px-5 py-3 whitespace-nowrap font-medium text-slate-900 truncate max-w-xs">

                    {txn.description}

                  </td>

                  <td className="px-5 py-3 whitespace-nowrap">{txn.category}</td>

                  <td className="px-5 py-3 whitespace-nowrap font-semibold text-slate-900">

                    {formatCurrency(parseFloat(txn.amount), 2)}

                  </td>

                  <td className="px-5 py-3 whitespace-nowrap text-right">

                    <button

                      onClick={() => onEditTransaction?.(txn.id)}

                      className="inline-flex items-center gap-2 rounded-full bg-slate-900/5 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-900/10"

                    >

                      Edit

                      <ArrowUpRight size={14} />

                    </button>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>

    </div>

  );

};



const HeroBanner = ({ totalSpend, transactionCount, topCategory, topBank, onRefresh }) => (
  <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-8 text-white shadow-panel">
    <div className="absolute inset-y-0 right-0 hidden w-1/2 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.25),transparent_55%)] opacity-40 lg:block" />
    <div className="absolute -left-10 top-10 h-32 w-32 rounded-full bg-brand-500/40 blur-3xl" />
    <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
      <div>
        <p className="text-xs uppercase tracking-[0.35em] text-white/70">Live snapshot</p>
        <h2 className="mt-3 font-display text-4xl">{formatCurrency(totalSpend, 0)}</h2>
        <p className="text-white/70">{transactionCount} transactions tracked</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            onClick={onRefresh}
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-4 py-2 text-sm font-semibold text-white transition hover:border-white hover:bg-white/10"
          >
            <RefreshCcw size={16} className="animate-spin-slow" />
            Refresh data
          </button>
        </div>
      </div>
      <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:w-auto">
        <HeroStat
          label="Top category"
          value={topCategory?.name || 'TBD'}
          helper={topCategory ? formatCurrency(topCategory.value, 0) : 'Upload more data'}
        />
        <HeroStat
          label="Primary bank"
          value={topBank?.bank || 'TBD'}
          helper={topBank ? formatCurrency(topBank.value, 0) + ' in spend' : 'Waiting on statements'}
        />
      </div>
    </div>
  </div>
);

const HeroStat = ({ label, value, helper }) => (
  <div className="rounded-2xl border border-white/20 bg-white/5 px-4 py-3 text-left text-white shadow-inner backdrop-blur">
    <p className="text-xs uppercase tracking-[0.25em] text-white/70">{label}</p>
    <p className="mt-2 text-xl font-semibold">{value}</p>
    <p className="text-xs text-white/70">{helper}</p>
  </div>
);

const StatCard = ({ title, value, icon, helper, tone = 'brand' }) => {
  const Icon = icon;
  return (
    <motion.div
      whileHover={{ translateY: -4 }}
      className="relative overflow-hidden rounded-3xl border border-white/30 bg-white/90 p-6 shadow-panel"
    >
      <div
        className={clsx(
          'mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white',
          toneClasses[tone]
        )}
      >
        <Icon size={22} />
      </div>
      <p className="text-sm font-semibold uppercase tracking-widest text-slate-500">{title}</p>
      <p className="mt-2 text-3xl font-semibold text-slate-900">{value}</p>
      {helper && <p className="text-xs text-slate-500">{helper}</p>}
      <ArrowUpRight className="absolute -right-1 -top-1 text-slate-200" size={24} />
    </motion.div>
  );
};

const toneClasses = {
  brand: 'bg-gradient-to-br from-brand-500 to-fuchsia-500',
  sky: 'bg-gradient-to-br from-sky-500 to-cyan-400',
  teal: 'bg-gradient-to-br from-emerald-500 to-teal-400',
  amber: 'bg-gradient-to-br from-amber-400 to-rose-400',
};

const ChartCard = ({ title, children }) => (
  <motion.div
    whileHover={{ translateY: -4 }}
    className="rounded-3xl border border-white/30 bg-white/95 p-6 shadow-panel backdrop-blur-xl"
  >
    <div className="flex items-center justify-between">
      <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
      <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">LIVE</span>
    </div>
    <div className="mt-4">{children}</div>
  </motion.div>
);



const rangeOptions = [

  { value: 'all', label: 'All Time' },

  { value: '30d', label: 'Last 30 Days' },

  { value: '90d', label: 'Last 90 Days' },

  { value: 'ytd', label: 'Year to Date' },

  { value: 'custom', label: 'Custom Range' },

];








const BudgetProgress = ({ budgets }) => {
  if (!budgets || budgets.length === 0) {
    return null;
  }

  return (
    <motion.div
      whileHover={{ translateY: -2 }}
      className="rounded-3xl border border-white/30 bg-white/95 p-6 shadow-panel"
    >
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-500">Budgets</p>
          <h3 className="text-2xl font-semibold text-slate-900">Budget Tracker</h3>
          <p className="text-sm text-slate-500">Monitor how each category is pacing against its target.</p>
        </div>
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
          {budgets.length} categories
        </span>
      </div>

      <div className="mt-6 space-y-4">
        {budgets.map((item) => (
          <div
            key={item.category}
            className="rounded-2xl border border-slate-100 bg-white/80 p-4 shadow-inner"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-semibold text-slate-900">{item.category}</p>
                <p className="text-xs text-slate-500">
                  Target {formatCurrency(item.limitAmount ?? item.limit ?? 0, 0)} · Actual{' '}
                  {formatCurrency(item.actualSpend ?? item.actual ?? 0, 0)}
                </p>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                {item.period}
              </span>
            </div>
            <div className="mt-3 h-3 rounded-full bg-slate-200/70">
              <div
                className={clsx('h-3 rounded-full transition-all', getBudgetStatusColor(item.status))}
                style={{ width: `${Math.min(item.percentUsedRaw ?? item.percentUsed ?? 0, 100)}%` }}
              />
            </div>
            <div className="mt-2 flex justify-between text-xs text-slate-500">
              <span>{getBudgetStatusLabel(item)}</span>
              <span>{(item.percentUsedRaw ?? item.percentUsed ?? 0).toFixed(1)}% used</span>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const InflowOutflowCard = ({ data }) => {
  if (!data) return null;
  const { inflow = 0, outflow = 0, transfer = 0, net = 0 } = data;
  const metrics = [
    { label: 'Inflow', value: inflow, color: 'text-emerald-600', accent: 'bg-emerald-100' },
    { label: 'Outflow', value: outflow, color: 'text-rose-600', accent: 'bg-rose-100' },
    { label: 'Transfers', value: transfer, color: 'text-amber-600', accent: 'bg-amber-100' },
    { label: 'Net', value: net, color: net >= 0 ? 'text-emerald-700' : 'text-rose-700', accent: 'bg-slate-100' },
  ];

  return (
    <motion.div
      whileHover={{ translateY: -4 }}
      className="rounded-3xl border border-white/30 bg-white/95 p-6 shadow-panel backdrop-blur"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-500">Cash pulse</p>
          <h3 className="text-2xl font-semibold text-slate-900">Cash Flow</h3>
          <p className="text-sm text-slate-500">Track recent money in versus money out.</p>
        </div>
        <div className="rounded-2xl bg-slate-900/5 p-3 text-slate-700">
          <PiggyBank size={20} />
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className="rounded-2xl border border-slate-100 bg-white/80 p-4 text-center shadow-inner"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{metric.label}</p>
            <p className={clsx('mt-2 text-2xl font-semibold', metric.color)}>
              {formatCurrency(metric.value, 0)}
            </p>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const AccountBalancesPanel = ({ accounts = [], onSnapshotSaved = () => {} }) => {
  const [form, setForm] = useState({
    accountLabel: '',
    bank: '',
    accountType: 'checking',
    balance: '',
    transactionBankKey: '',
    capturedAt: '',
    note: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const hasAccounts = accounts && accounts.length > 0;

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSnapshotSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await axios.post('/api/accounts/snapshots', {
        accountLabel: form.accountLabel,
        bank: form.bank,
        accountType: form.accountType,
        balance: form.balance,
        transactionBankKey: form.transactionBankKey,
        capturedAt: form.capturedAt || undefined,
        note: form.note,
      });
      setForm({
        accountLabel: '',
        bank: '',
        accountType: 'checking',
        balance: '',
        transactionBankKey: '',
        capturedAt: '',
        note: '',
      });
      onSnapshotSaved();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save snapshot.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-3xl border border-white/30 bg-white/95 p-6 shadow-panel mb-8">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-800">Account Balances</h3>
          <p className="text-sm text-gray-500">
            Capture snapshots of checking and savings accounts to keep your dashboard aligned with reality.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSnapshotSubmit} className="grid gap-4 md:grid-cols-2 mb-6">
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">
            Account Label
          </label>
          <input
            type="text"
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="e.g., Wells Fargo Checking"
            value={form.accountLabel}
            onChange={(e) => handleFormChange('accountLabel', e.target.value)}
            required
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">
            Bank / Institution
          </label>
          <input
            type="text"
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Wells Fargo"
            value={form.bank}
            onChange={(e) => handleFormChange('bank', e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">
            Account Type
          </label>
          <select
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={form.accountType}
            onChange={(e) => handleFormChange('accountType', e.target.value)}
          >
            <option value="checking">Checking</option>
            <option value="savings">Savings</option>
            <option value="brokerage">Brokerage</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">
            Transaction Bank Key
          </label>
          <input
            type="text"
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Match the bank name appearing in uploads (optional)"
            value={form.transactionBankKey}
            onChange={(e) => handleFormChange('transactionBankKey', e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">
            Balance
          </label>
          <input
            type="number"
            step="0.01"
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="0.00"
            value={form.balance}
            onChange={(e) => handleFormChange('balance', e.target.value)}
            required
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">
            Snapshot Date
          </label>
          <input
            type="datetime-local"
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={form.capturedAt}
            onChange={(e) => handleFormChange('capturedAt', e.target.value)}
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 inline-block">
            Notes
          </label>
          <textarea
            className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            rows="2"
            value={form.note}
            onChange={(e) => handleFormChange('note', e.target.value)}
          />
        </div>
        <div className="md:col-span-2 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md shadow hover:bg-indigo-500 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Snapshot'}
          </button>
        </div>
      </form>

      {hasAccounts ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-2 text-left">Account</th>
                <th className="px-4 py-2 text-left">Bank</th>
                <th className="px-4 py-2 text-right">Snapshot</th>
                <th className="px-4 py-2 text-right">Net Change</th>
                <th className="px-4 py-2 text-right">Current</th>
                <th className="px-4 py-2 text-left">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {accounts.map((account) => (
                <tr key={`${account.accountLabel}-${account.snapshotCapturedAt || account.id}`}>
                  <td className="px-4 py-2 text-gray-800 font-medium">{account.accountLabel}</td>
                  <td className="px-4 py-2 text-gray-600">{account.bank || '-'}</td>
                  <td className="px-4 py-2 text-right text-gray-700">
                    {formatCurrency(account.snapshotBalance, 2)}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700">
                    {formatCurrency(account.netChange, 2)}
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-gray-900">
                    {formatCurrency(account.currentBalance, 2)}
                  </td>
                  <td className="px-4 py-2 text-gray-500 text-xs">
                    {formatDateTimeDisplay(account.snapshotCapturedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-gray-500">No account snapshots yet. Add one above to get started.</p>
      )}
    </div>
  );
};





const TopMerchantsTable = ({ merchants = [] }) => (
  <div className="rounded-3xl border border-white/30 bg-white/95 p-6 shadow-panel">
    <div className="flex items-center justify-between">
      <h3 className="text-xl font-semibold text-slate-900">Top Merchants</h3>
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
        {merchants.length} tracked
      </span>
    </div>
    {merchants.length === 0 ? (
      <p className="mt-4 text-sm text-slate-500">Upload statements to start seeing merchant insights.</p>
    ) : (
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100 text-sm">
          <thead className="bg-slate-50/70 text-slate-500 uppercase text-xs">
            <tr>
              <th className="px-4 py-2 text-left">Merchant</th>
              <th className="px-4 py-2 text-right">Total Spend</th>
              <th className="px-4 py-2 text-right">Transactions</th>
              <th className="px-4 py-2 text-right">Avg.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {merchants.map((merchant) => (
              <tr key={merchant.name} className="hover:bg-slate-50/70">
                <td className="px-4 py-2 font-medium text-slate-900">{merchant.name}</td>
                <td className="px-4 py-2 text-right text-slate-900">{formatCurrency(merchant.total, 2)}</td>
                <td className="px-4 py-2 text-right text-slate-600">{merchant.transactions}</td>
                <td className="px-4 py-2 text-right text-slate-600">{formatCurrency(merchant.average, 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);
const BudgetCoachPanel = ({ insight }) => {

  if (!insight) return null;

  const { coachSummary, categoryTips = [] } = insight;

  if (!coachSummary && categoryTips.length === 0) {

    return null;

  }



  const statusStyles = {

    over: 'bg-red-100 text-red-800',

    warning: 'bg-yellow-100 text-yellow-800',

  };



  return (

    <div className="bg-white p-6 rounded-lg shadow-xl border border-indigo-100 mb-8">

      <div className="flex items-center space-x-3">

        <div className="rounded-full bg-indigo-50 p-2">

          <Sparkles className="text-indigo-600" size={20} />

        </div>

        <div>

          <h3 className="text-lg font-semibold text-gray-800">AI Budget Coach</h3>

          <p className="text-sm text-gray-500">Gemini looks at categories that need attention and suggests next moves.</p>

        </div>

      </div>

      {coachSummary && <p className="mt-4 text-gray-700">{coachSummary}</p>}

      {categoryTips.length > 0 && (

        <div className="mt-5 space-y-4">

          {categoryTips.map((tip, idx) => (

            <div key={`${tip.category}-${idx}`} className="rounded-md border border-gray-100 bg-gray-50 p-4">

              <div className="flex items-center justify-between mb-2">

                <p className="font-semibold text-gray-800">{tip.category}</p>

                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[tip.status] || 'bg-gray-100 text-gray-800'}`}>

                  {tip.status === 'over' ? 'Over target' : 'Watch list'}

                </span>

              </div>

              <p className="text-sm text-gray-700">{tip.tip}</p>

              {tip.suggestedActions && tip.suggestedActions.length > 0 && (

                <ul className="mt-2 text-sm text-gray-600 list-disc list-inside space-y-1">

                  {tip.suggestedActions.map((action, actionIdx) => (

                    <li key={`${tip.category}-action-${actionIdx}`}>{action}</li>

                  ))}

                </ul>

              )}

            </div>

          ))}

        </div>

      )}

    </div>

  );

};
const InsightsPanel = ({ budgets = [], categories = [], topBank }) => {
  const overspent = budgets.filter((budget) => budget.status === 'over');
  const watchList = budgets.filter((budget) => budget.status === 'warning');
  const topCategory = categories[0];

  return (
    <div className="rounded-3xl border border-white/30 bg-white/95 p-6 shadow-panel">
      <h3 className="text-xl font-semibold text-slate-900">Spending Insights</h3>
      <ul className="mt-4 space-y-4 text-sm text-slate-600">
        {topCategory && (
          <li className="flex items-start gap-3 rounded-2xl border border-indigo-100/80 bg-indigo-50/60 p-3">
            <span className="mt-1 h-2 w-2 rounded-full bg-indigo-500" />
            <div>
              <p className="font-semibold text-slate-900">Highest category this period</p>
              <p>
                {topCategory.name}: {formatCurrency(topCategory.value, 0)}
              </p>
            </div>
          </li>
        )}
        {topBank && (
          <li className="flex items-start gap-3 rounded-2xl border border-sky-100/80 bg-sky-50/70 p-3">
            <span className="mt-1 h-2 w-2 rounded-full bg-sky-500" />
            <div>
              <p className="font-semibold text-slate-900">Primary bank</p>
              <p>
                {topBank.bank}: {formatCurrency(topBank.value, 0)} spent
              </p>
            </div>
          </li>
        )}
        {overspent.length > 0 && (
          <li className="flex items-start gap-3 rounded-2xl border border-rose-100/80 bg-rose-50/70 p-3">
            <span className="mt-1 h-2 w-2 rounded-full bg-rose-500" />
            <div>
              <p className="font-semibold text-slate-900">Over budget</p>
              <p>
                {overspent
                  .map((item) => `${item.category} (${formatCurrency(Math.abs(item.remaining), 0)} over)`)
                  .join(', ')}
              </p>
            </div>
          </li>
        )}
        {watchList.length > 0 && (
          <li className="flex items-start gap-3 rounded-2xl border border-amber-100/80 bg-amber-50/60 p-3">
            <span className="mt-1 h-2 w-2 rounded-full bg-amber-500" />
            <div>
              <p className="font-semibold text-slate-900">Close to the limit</p>
              <p>
                {watchList.map((item) => `${item.category} (${formatCurrency(item.remaining, 0)} left)`).join(', ')}
              </p>
            </div>
          </li>
        )}
        {budgets.length > 0 && overspent.length === 0 && watchList.length === 0 && (
          <li className="flex items-start gap-3 rounded-2xl border border-emerald-100/80 bg-emerald-50/70 p-3">
            <span className="mt-1 h-2 w-2 rounded-full bg-emerald-500" />
            <div>
              <p className="font-semibold text-slate-900">Budgets on track</p>
              <p>Great job! Every category is pacing below its target.</p>
            </div>
          </li>
        )}
      </ul>
    </div>
  );
};
const getBudgetStatusColor = (status) => {

  if (status === 'over') return 'bg-red-500';

  if (status === 'warning') return 'bg-yellow-500';

  return 'bg-indigo-500';

};
const getBudgetStatusLabel = (item) => {

  if (item.status === 'over') return `${formatCurrency(Math.abs(item.remaining), 0)} over budget`;

  if (item.status === 'warning') return `${formatCurrency(item.remaining, 0)} left - track closely`;

  return `${formatCurrency(item.remaining, 0)} left`;

};
export default Dashboard;
